import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, connectAuthEmulator, sendSignInLinkToEmail, isSignInWithEmailLink } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

// Your web app's Firebase configuration
// For security, these values should be in a `.env.local` file
// You can also disable Firebase entirely via REACT_APP_FIREBASE_DISABLED=true
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const missingConfigKeys = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => key);
const isDisabled = String(process.env.REACT_APP_FIREBASE_DISABLED || '').toLowerCase() === 'true';
const useEmulators = String(process.env.REACT_APP_FIREBASE_USE_EMULATORS || '').toLowerCase() === 'true';
const hasValidConfig = !isDisabled && missingConfigKeys.length === 0;

let app = null;

if (hasValidConfig) {
  const apps = getApps();
  app = apps.length ? getApp() : initializeApp(firebaseConfig);
} else if (useEmulators && !isDisabled) {
  // Allow minimal config when using emulators (projectId is sufficient)
  const emulatorConfig = {
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'demo-project',
  };
  const apps = getApps();
  app = apps.length ? getApp() : initializeApp(emulatorConfig);
} else if (process.env.NODE_ENV !== 'production') {
  console.warn(
    '[firebase] Skipping initialization because the following env vars are missing:',
    missingConfigKeys,
  );
}

const createFirestoreInstance = (firebaseApp) => {
  if (!firebaseApp) {
    return null;
  }

  const firestoreSettings = {
    experimentalAutoDetectLongPolling: true,
    useFetchStreams: false,
  };

  if (process.env.REACT_APP_FIREBASE_FORCE_LONG_POLLING === 'true') {
    delete firestoreSettings.experimentalAutoDetectLongPolling;
    firestoreSettings.experimentalForceLongPolling = true;
  }

  const databaseId = (process.env.REACT_APP_FIREBASE_DATABASE_ID || '').trim() || undefined;
  return initializeFirestore(firebaseApp, firestoreSettings, databaseId);
};

const auth = app ? getAuth(app) : null;
const db = createFirestoreInstance(app);
const storage = app ? getStorage(app) : null;
const appCheckSiteKey = String(
  process.env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY
  || process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY
  || ''
).trim();
const appCheck = app && appCheckSiteKey && !useEmulators
  ? initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    })
  : null;

// Connect to emulators if requested
let emulatorInfo = null;
if (app && useEmulators) {
  const parseHostPort = (host, port, fallbackPort) => {
    if (host && host.includes(':')) {
      const [h, p] = host.split(':');
      return { host: h, port: Number(p) || fallbackPort };
    }
    return { host: host || '127.0.0.1', port: Number(port) || fallbackPort };
  };

  // Firestore
  if (db) {
    const { host: fsHost, port: fsPort } = parseHostPort(
      process.env.REACT_APP_FIRESTORE_EMULATOR_HOST,
      process.env.REACT_APP_FIRESTORE_EMULATOR_PORT,
      8080
    );
    try {
      connectFirestoreEmulator(db, fsHost, fsPort);
    } catch (_) {}
    emulatorInfo = { ...(emulatorInfo || {}), firestore: { host: fsHost, port: fsPort } };
  }

  // Auth
  if (auth) {
    const { host: authHost, port: authPort } = parseHostPort(
      process.env.REACT_APP_AUTH_EMULATOR_HOST,
      process.env.REACT_APP_AUTH_EMULATOR_PORT,
      9099
    );
    const authUrl = `http://${authHost}:${authPort}`;
    try {
      connectAuthEmulator(auth, authUrl, { disableWarnings: true });
    } catch (_) {}
    emulatorInfo = { ...(emulatorInfo || {}), auth: { host: authHost, port: authPort } };
  }

  // Storage
  if (storage) {
    const { host: stHost, port: stPort } = parseHostPort(
      process.env.REACT_APP_STORAGE_EMULATOR_HOST,
      process.env.REACT_APP_STORAGE_EMULATOR_PORT,
      9199
    );
    try {
      connectStorageEmulator(storage, stHost, stPort);
    } catch (_) {}
    emulatorInfo = { ...(emulatorInfo || {}), storage: { host: stHost, port: stPort } };
  }
}

const firebaseServices = {
  auth,
  db,
  storage,
  isConfigured: Boolean(app),
  missingConfigKeys,
  config: hasValidConfig ? firebaseConfig : null,
  disabled: isDisabled,
  emulators: useEmulators ? (emulatorInfo || {}) : null,
};

export default firebaseServices;
export { app, auth, appCheck, db, storage, sendSignInLinkToEmail, isSignInWithEmailLink };
